import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Anasayfa', href: '/' },
    { name: 'Hakkımızda', href: '/hakkimizda' },
    { 
      name: 'Hizmetler', 
      href: '/hizmetler',
      hasSubmenu: true,
      submenu: [
        {
          name: 'CNC Torna',
          href: '/hizmetler/cnc-torna',
          icon: 'ri-settings-3-line',
          description: 'Milimetrik hassasiyet ile çok operasyonlu işleme'
        },
        {
          name: 'CNC Dik İşleme',
          href: '/hizmetler/cnc-dik-isleme',
          icon: 'ri-tools-line',
          description: 'Kompleks geometri ve yüksek yüzey kalitesi'
        },
        {
          name: 'Üniversal Torna & Freze',
          href: '/hizmetler/universal-torna-freze',
          icon: 'ri-hammer-line',
          description: 'Manuel esneklik ile düşük adetli işler'
        },
        {
          name: 'Kaynak Hizmetleri',
          href: '/hizmetler/kaynak',
          icon: 'ri-flashlight-line',
          description: 'TIG, MIG, punta kaynak ile estetik ve dayanıklılık'
        },
        {
          name: '3D Printer Atölyesi',
          href: '/hizmetler/3d-printer',
          icon: 'ri-printer-line',
          description: 'Hızlı prototipleme ile FDM/SLA sistemleri'
        }
      ]
    },
    { name: 'Kalite', href: '/kalite' },
    { name: 'Blog', href: '/blog' },
    { name: 'İletişim', href: '/iletisim' },
  ];

  const handleLinkClick = () => {
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={handleLinkClick} className="flex items-center space-x-3 group">
            <img 
              src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/71d8726e11bf2c1882a36ade8cdcc994.png" 
              alt="3DP Ankara Logo" 
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                {item.hasSubmenu ? (
                  <>
                    <button
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center ${
                        location.pathname.startsWith('/hizmetler') 
                          ? 'text-[#C1121F] bg-red-50' 
                          : 'text-gray-700 hover:text-[#C1121F] hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      {item.name}
                      <i className="ri-arrow-down-s-line ml-1 text-sm transition-transform duration-300 group-hover:rotate-180"></i>
                    </button>
                    
                    {/* Submenu */}
                    <div 
                      className={`absolute top-full left-0 w-96 bg-white shadow-xl rounded-2xl border border-gray-100 py-4 mt-2 transition-all duration-300 ${
                        isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <div className="px-6 pb-3 border-b border-gray-100">
                        <Link 
                          to="/hizmetler"
                          onClick={handleLinkClick}
                          className="text-lg font-semibold text-gray-800 hover:text-[#C1121F] transition-colors flex items-center"
                        >
                          <i className="ri-service-line mr-2 text-[#C1121F]"></i>
                          Tüm Hizmetler
                        </Link>
                      </div>
                      {item.submenu?.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          onClick={handleLinkClick}
                          className="flex items-start px-6 py-4 hover:bg-gray-50 transition-all duration-300 group/item"
                        >
                          <div className="w-12 h-12 bg-gradient-to-br from-[#C1121F] to-[#A00E1A] rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/item:scale-110 transition-transform duration-300">
                            <i className={`${subItem.icon} text-white text-lg`}></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 group-hover/item:text-[#C1121F] transition-colors">
                              {subItem.name}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                              {subItem.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    onClick={handleLinkClick}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                      location.pathname === item.href 
                        ? 'text-[#C1121F] bg-red-50' 
                        : 'text-gray-700 hover:text-[#C1121F] hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/teklif-al"
              onClick={handleLinkClick}
              className="bg-[#C1121F] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#A00E1A] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap flex items-center"
            >
              <i className="ri-file-text-line mr-2"></i>
              Teklif Al
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-all duration-300"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-700`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-gray-100">
            <nav className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasSubmenu ? (
                    <>
                      <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className={`text-sm font-medium transition-all duration-300 hover:text-[#C1121F] flex items-center justify-between w-full p-3 rounded-lg hover:bg-gray-50 ${
                          location.pathname.startsWith('/hizmetler') ? 'text-[#C1121F] bg-red-50' : 'text-gray-700'
                        }`}
                      >
                        <span className="flex items-center">
                          <i className="ri-service-line mr-2"></i>
                          {item.name}
                        </span>
                        <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      
                      {isServicesOpen && (
                        <div className="mt-2 ml-4 space-y-1">
                          <Link
                            to="/hizmetler"
                            onClick={handleLinkClick}
                            className="block text-sm font-medium text-[#C1121F] hover:text-[#A00E1A] transition-colors p-2 rounded-lg hover:bg-red-50"
                          >
                            Tüm Hizmetler
                          </Link>
                          {item.submenu?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={handleLinkClick}
                              className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-all duration-300"
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-[#C1121F] to-[#A00E1A] rounded-lg flex items-center justify-center mr-3">
                                <i className={`${subItem.icon} text-white text-sm`}></i>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-800 text-sm">
                                  {subItem.name}
                                </h4>
                                <p className="text-xs text-gray-600 mt-1">
                                  {subItem.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={handleLinkClick}
                      className={`text-sm font-medium transition-all duration-300 hover:text-[#C1121F] p-3 rounded-lg hover:bg-gray-50 flex items-center ${
                        location.pathname === item.href ? 'text-[#C1121F] bg-red-50' : 'text-gray-700'
                      }`}
                    >
                      <i className="ri-arrow-right-s-line mr-2"></i>
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to="/teklif-al"
                onClick={handleLinkClick}
                className="bg-[#C1121F] text-white px-6 py-4 rounded-full font-semibold hover:bg-[#A00E1A] transition-all duration-300 text-center whitespace-nowrap shadow-lg flex items-center justify-center mt-4"
              >
                <i className="ri-file-text-line mr-2"></i>
                Teklif Al
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
